import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIonToast, useIonViewWillEnter } from "@ionic/react";
import { ShippingAddressComponent } from "../../components/ShippingAddressComponent/ShippingAddressComponent";
import ContinueButton from "../../components/ContinueButton/ContinueButton";
import { useHistory } from "react-router";
import { DatetimeChangeEventDetail } from "@ionic/core/components";
import { getCurrentCurrencySymbol, isPhoneNumberValid } from "../../utils";
import { PaymentTabButton, ShippingTabButton } from "../../types";
import { getPaymentTypeIcon, getShippingTypeIcon } from "../../providers/utils/utils";
import { useConfig } from "../../providers/config";
import {
  PaymentMethod,
  PaymentMethodIdEnum,
  ShippingMethod,
  ShippingMethodDeliveryType,
  ShippingMethodType,
} from "@core/types";
import {
  useGetPaymentMethodsQuery,
  useGetPickupPoints,
  useGetShippingMethodsQuery,
} from "../../providers/shippingAndPayment";
import { useAuth } from "../../contexts/auth-context";
import { OrderDataState, useOrderData } from "../../providers/orderDataStore";
import { useCartData } from "../../providers/cartDataStore";
import { useActiveOrMainShippingZone } from "../../providers/shippingZones";
import { useUpdateProfileMutation } from "../../providers/profile";
import { useAuthBySms, useGetSmsCode } from "../../providers/Auth";
import { useSmsVerificationData } from "../../providers/smsVerifivation";
import { AuthByCode } from "../../components/AuthByCode/AuthByCode";
import { useMarketMode } from "../../providers/marketMode";
import Page from "../page-layout";
import useIsViewFocused from "../../hooks/useIsViewFocused";
import { useTranslation } from "react-i18next";
import { useHandleCustomerLogin } from "../../hooks/useHandleCustomerLogin";
import useGetCurrentAddress from "../../hooks/useGetCurrentAddress";
import { getEnabledShippingMethods, filterShippingMethodsByCartTotal } from "../../utils";
import { checkShippingZone } from "../../utils/checkShippingZone";

const ALLOW_GUEST_ORDER = process.env.REACT_APP_ALLOW_GUEST_ORDER === "yes";

const ShippingAddressPage = () => {
  let history = useHistory();
  const { t } = useTranslation();

  const { user, updateUserData, isUserAuthenticated } = useAuth();
  const config = useConfig();

  const { activeShippingZone, isLoading: isActiveShippingZoneLoading } = useActiveOrMainShippingZone();

  const [presentToast] = useIonToast();
  const [shippingTabsButtons, setShippingTabsButtons] = useState<ShippingTabButton[]>([]);
  const [paymentTabsButtons, setPaymentTabsButtons] = useState<PaymentTabButton[]>([]);
  const [isCodeValid, setIsCodeValid] = useState(true);
  const [present] = useIonToast();
  const isViewFocused = useIsViewFocused();

  const [isSmsAlertOpened, setIsAlertSmsOpened] = useState(false);
  const verificationCodeData = useRef<{ code_id: string; phone_number: string } | null>(null);

  const { mutate, isLoading: isGettingSmsCodeLoading } = useGetSmsCode();
  const { mutate: mutateAuth, isLoading: isAuthLoading } = useAuthBySms();
  const { setWaitingTimer, canGetNewCode, sentSmsAgain, phoneNumber: prevPhoneNumber } = useSmsVerificationData();
  const handleCustomerLogin = useHandleCustomerLogin();

  const { mode } = useMarketMode();

  const {
    shippingMethod,
    firstName,
    lastName,
    phoneNumber,
    updateOrderData,
    paymentMethod,
    deliveryDate,
    deliveryInterval,
  } = useOrderData();

  const { getProductsTotalWeight, cartSubtotalValue } = useCartData();

  const handlePaymentMethods = (paymentMethods?: PaymentMethod[]) => {
    if (paymentMethods) {
      const enabledPaymentMethods = paymentMethods.filter((payment: PaymentMethod) => payment.enabled);
      const enabledTabsButtons = enabledPaymentMethods.map((payment) => {
        return {
          id: payment.id,
          icon: getPaymentTypeIcon(payment.type as PaymentMethodIdEnum),
          title: payment.title,
        };
      });

      setPaymentTabsButtons(enabledTabsButtons);
    }
  };

  const { data: paymentMethods, isFetching: isPaymentMethodsLoading } = useGetPaymentMethodsQuery({
    onError: () => {
      presentToast({
        message: t("unexpectedErrorPaymentMethods"),
        duration: 2000,
        color: "danger",
      });
      history.goBack();
    },
    onSuccess: handlePaymentMethods,
  });

  useEffect(() => {
    handlePaymentMethods(paymentMethods);
  }, [paymentMethods]);

  const handleShippingMethods = (shippingMethods?: ShippingMethod[]) => {
    if (shippingMethods) {
      setShippingTabsButtons(getShippingButtons(shippingMethods));
    }
  };

  const shouldFetchShippingMethods =
    ((config && !config.shipping.isShippingZonesEnabled) || !!activeShippingZone?.id) && isViewFocused;

  const { data: shippingMethods, isFetching: isShippingMethodsLoading } = useGetShippingMethodsQuery(
    String(activeShippingZone?.id || ""),
    getProductsTotalWeight(),
    {
      enabled: shouldFetchShippingMethods,
      onError: () => {
        presentToast({
          message: t("unexpectedErrorShippingMethods"),
          duration: 2000,
          color: "danger",
        });
        history.goBack();
      },
      onSuccess: handleShippingMethods,
    },
  );

  useEffect(() => {
    handleShippingMethods(shippingMethods);
  }, [shippingMethods, cartSubtotalValue]);

  const { data: pickupPoints } = useGetPickupPoints({
    zoneId: activeShippingZone.id,
    options: {
      enabled: shippingMethod?.methodType === ShippingMethodType.pickupPoint && !!activeShippingZone.id,
    },
  });

  const [isPhoneNumberInputValid, setIsPhoneNumberInputValid] = useState(false);
  const [isWhatsappPhoneNumberValid, setIsWhatsappPhoneNumberValid] = useState(true);

  const updateState = async () => {
    if (user) {
      const orderData: Partial<OrderDataState> = {
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phone,
        whatsAppNumber: user.phone,
        // shippingMethod: null,
        // paymentMethod: null,
        deliveryInterval: config?.isDeliveryIntervalEnabled ? deliveryInterval : undefined,
      };

      updateOrderData(orderData);
    }

    if (user?.phone) {
      setIsPhoneNumberInputValid(isPhoneNumberValid(user.phone));
    }
  };

  const toggleIsSmsAlertOpened = useCallback(() => {
    setIsAlertSmsOpened((prev) => !prev);
  }, [isSmsAlertOpened]);

  const authUserBySms = (code: string) => {
    if (!verificationCodeData.current) return;

    mutateAuth(
      {
        firstName: firstName,
        lastName: lastName,
        phone: verificationCodeData.current?.phone_number,
        verificationCode: code,
        verificationCodeId: verificationCodeData.current?.code_id,
      },
      {
        onError: (error) => {
          if (error?.response?.data?.message === "Verification code data is not valid") {
            present({
              color: "danger",
              message: t("Invalid verification code"),
              duration: 1500,
              position: "bottom",
            });
            setIsCodeValid(false);
          }

          console.log(error, "error");
        },
        onSuccess: async (data) => {
          await handleCustomerLogin(data);

          toggleIsSmsAlertOpened();
          proceedOrder();

          // updateOrderData({
          //   address: address,
          // });
        },
      },
    );
  };

  const handleAuth = () => {
    if (isUserAuthenticated) {
      proceedOrder();
      return;
    }

    if (!canGetNewCode) {
      toggleIsSmsAlertOpened();
      return;
    }

    if (prevPhoneNumber !== phoneNumber) {
      getCode(phoneNumber, () => {
        setWaitingTimer(phoneNumber);
        toggleIsSmsAlertOpened();
      });
    } else {
      toggleIsSmsAlertOpened();
    }
  };

  const requestNewCode = () => {
    if (!canGetNewCode) return;

    getCode(phoneNumber, sentSmsAgain);
  };

  const getCode = (phone: string, onSucceedCb: () => void) => {
    mutate(
      { phone },
      {
        onError: (error) => {
          console.log(error, "on error");
        },
        onSuccess: (data) => {
          verificationCodeData.current = {
            code_id: data.verificationCodeId,
            phone_number: data.phoneNumber,
          };
          onSucceedCb();
          // AppMetricaProvider.reportEvent("login", { ...data, token: undefined });
          return data;
        },
      },
    );
  };

  const handleSubmit = () => {
    if (ALLOW_GUEST_ORDER) {
      proceedOrder();
    } else {
      handleAuth();
    }
  };

  useIonViewWillEnter(() => {
    setIsPhoneNumberInputValid(isPhoneNumberValid(phoneNumber));
    updateState();
    handleShippingMethods(shippingMethods);
  }, [
    setIsPhoneNumberInputValid,
    isPhoneNumberValid,
    updateState,
    handleShippingMethods,
    phoneNumber,
    cartSubtotalValue,
    user,
    shippingMethods,
  ]);

  const getShippingButtons = (shippingMethods: ShippingMethod[]) => {
    const enabledMethods = getEnabledShippingMethods(shippingMethods);
    const activeMethods = filterShippingMethodsByCartTotal({
      cartSubtotalValue,
      methods: enabledMethods,
    });
    const buttons = activeMethods.map((method: ShippingMethod): ShippingTabButton => {
      return {
        id: method.methodId,
        icon: getShippingTypeIcon(method.methodType),
        title:
          parseFloat(method.cost ?? "") > 0
            ? parseFloat(method.minAmount ?? "") > 0
              ? `${method.title} ${method.cost}${getCurrentCurrencySymbol()}`
              : `${method.title} \n ${t("from")} ${method.cost}${getCurrentCurrencySymbol()}`
            : method.title,
        description:
          parseFloat(method.minAmount ?? "") > 0 ? `${t("from")} ${method.minAmount}${getCurrentCurrencySymbol()}` : "",
        disabled: parseFloat(method.minAmount ?? "") > cartSubtotalValue,
      };
    });

    const activeButtons = buttons.filter((b) => !b.disabled);
    if (
      activeButtons.length === 1 ||
      (config?.addressSettings.addressInputDependsOnShippingMethod && activeButtons.length > 0)
    ) {
      const method =
        activeMethods.find((activeMethod) =>
          activeButtons.some((activeButtonAmount) => activeButtonAmount.id === activeMethod.id),
        ) || (activeButtons.length === 1 ? activeMethods[0] : undefined);

      // if (!method) {
      // throw new Error("Подходящего метода нет");
      // };

      updateOrderData({
        shippingMethod: method,
      });
    }

    return buttons;
  };

  const setActiveShippingMethod = (methodId: string) => {
    const method: ShippingMethod = shippingMethods?.find((method) => method?.methodId === methodId)!;
    updateOrderData({ shippingMethod: method || null });
  };

  const setPayment = useCallback(
    (methodId: string) => {
      const findPaymentMethod = paymentMethods?.find((payment) => {
        return payment.id === methodId;
      });

      updateOrderData({
        paymentMethod: findPaymentMethod,
      });
    },
    [paymentMethods, updateOrderData],
  );
  const { mutate: updateProfile } = useUpdateProfileMutation();

  const proceedOrder = () => {
    console.log({ paymentMethod });
    if (user && firstName !== user.firstName) {
      const updatePayload = { firstName: firstName, lastName: lastName };
      updateProfile(
        { userId: user.id, userPayload: updatePayload },
        { onSuccess: () => updateUserData(updatePayload) },
      );
    }

    history.replace("/order");
  };

  const address = useGetCurrentAddress();

  function isFormValid() {
    if (config?.isExpressDeliveryEnabled && mode === "express") {
      return !!(firstName && phoneNumber && address && paymentMethod);
    }

    const cityOrAddress = address?.city || address?.fullAddress || "";
    const shippingZoneName = activeShippingZone?.name ?? "";

    const deliveryIntervalField = config?.isDeliveryIntervalEnabled ? deliveryDate && deliveryInterval : deliveryDate;

    return !!(
      firstName &&
      phoneNumber &&
      address &&
      shippingMethod &&
      paymentMethod &&
      (shippingMethod.methodDeliveryType === ShippingMethodDeliveryType.nextDay || deliveryIntervalField) &&
      // shippingZone?.name.includes(address.city ?? address.fullAddress ?? "")
      (!config.shipping.isShippingZonesEnabled || checkShippingZone(cityOrAddress, shippingZoneName))
    );
  }

  async function handleDeliveryDate(e: CustomEvent<DatetimeChangeEventDetail>) {
    updateOrderData({
      deliveryDate: e.detail.value ? new Date(String(e.detail.value.slice(0, 10))) : null,
      deliveryInterval: "",
    });
  }

  return (
    <Page>
      <Page.HeaderMobile showBackButton={true} />
      <Page.Content>
        <ShippingAddressComponent
          pickupPoints={pickupPoints}
          setPaymentMethod={setPayment}
          setShippingMethod={setActiveShippingMethod}
          shippingTabsButtons={shippingTabsButtons}
          paymentTabsButtons={paymentTabsButtons}
          submit={handleSubmit}
          handleDeliveryDate={handleDeliveryDate}
          isFormValid={isFormValid}
          isPhoneNumberInputValid={isPhoneNumberInputValid}
          isWhatsappPhoneNumberValid={isWhatsappPhoneNumberValid}
          setIsPhoneNumberInputValid={setIsPhoneNumberInputValid}
          setIsWhatsappPhoneNumberValid={setIsWhatsappPhoneNumberValid}
          // prettier-ignore
          showLoading={(isPaymentMethodsLoading || isShippingMethodsLoading || isAuthLoading || isActiveShippingZoneLoading) || isGettingSmsCodeLoading}
        />
        <AuthByCode
          isCodeValid={isCodeValid}
          isOpen={isSmsAlertOpened}
          toggleIsOpen={toggleIsSmsAlertOpened}
          auth={authUserBySms}
          requestNewCode={requestNewCode}
        />
      </Page.Content>

      <Page.PageFooter className={"oneButtonFooter"}>
        <div className={"ion-hide-md-up"}>
          <ContinueButton onClick={handleSubmit} disabled={!isFormValid()} buttonText={t("Next")} />
        </div>
      </Page.PageFooter>
    </Page>
  );
};

export { ShippingAddressPage };
